import React, { useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import {
  AccountManager,
  printMissingConfigError,
  SiteConfigManager,
  useSettings,
} from '@atlas/core';
import { Button, Flag } from '@atlas/ui';

import { languageTypeFlag, languageTypeISO } from 'common/constants';
import { CDN_TYPE_BASE, getCDNUrl } from 'common/utils';

import { DropdownLanguageWrapper } from './DropdownLanguageWrapper';

const LangItem = ({ cdnHostname, code, label, name, onClick, regionId }) => (
  <Button className="dropdown-list-item" onClick={() => onClick(code)} type="text">
    <div className="dropdown-button__flag">
      <Flag alt={name} cdnHostname={cdnHostname} height={15} name={regionId} width={22} />
    </div>
    <div className="dropdown-button__text">{label}</div>
  </Button>
);

LangItem.propTypes = {
  cdnHostname: PropTypes.string,
  code: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  label: PropTypes.string,
  name: PropTypes.string,
  onClick: PropTypes.func,
  regionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default function DropdownLanguage({ className }) {
  const languages = SiteConfigManager.getConfig('languages', []);
  const { lang } = useSettings();
  const [isExpanded, setExpanded] = useState(false);

  const desktopLanguageType = SiteConfigManager.getConfig(
    'config.config.language.desktopLanguageType',
  );

  const isLanguageTypeFlag = desktopLanguageType === languageTypeFlag;
  const isLanguageTypeISO = desktopLanguageType === languageTypeISO;

  const dropdownClassName = cx(
    className,
    { 'only-flag': isLanguageTypeFlag },
    { 'only-code': isLanguageTypeISO },
  );

  if (languages.length === 0) {
    printMissingConfigError('languages');
  }

  const setLanguage = React.useCallback(newLang => {
    AccountManager.updateUserLanguage(newLang);
  }, []);

  const cdnHostname = getCDNUrl(CDN_TYPE_BASE);
  const languageOptions = React.useMemo(
    () =>
      languages.map(({ code, name, regionId }) => (
        <LangItem
          cdnHostname={cdnHostname}
          code={code}
          key={code}
          label={code}
          name={name}
          onClick={setLanguage}
          regionId={regionId}
        />
      )),
    [setLanguage],
  );

  return (
    <DropdownLanguageWrapper
      buttonText={lang}
      className={dropdownClassName}
      isActive={isExpanded}
      isCloseOnClickInside
      setExpanded={setExpanded}
    >
      {languageOptions}
    </DropdownLanguageWrapper>
  );
}

DropdownLanguage.propTypes = {
  className: PropTypes.string,
};
